import React, { useState, useEffect } from 'react';
import {
  Search,
  Package,
  User,
  Calendar,
  Hash,
  Globe,
  Image,
  RefreshCw,
  X,
  Eye,
  FileText,
  Flag,
  Camera,
  Filter,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';
import { Button, Input, Card, Badge, LoadingSpinner, Notification } from './SharedComponents';
import moment from 'moment';

const API_URL = import.meta.env.VITE_API_URL || 'https://react-app-production-3114.up.railway.app';

function ASINCountryDatabase() {
  // State management
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [sortBy, setSortBy] = useState('updated');
  const [sortOrder, setSortOrder] = useState('desc');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  useEffect(() => {
    fetchASINData();
  }, []);

  useEffect(() => {
    handleFilterAndSort();
  }, [searchTerm, data, sortBy, sortOrder]);

  const fetchASINData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/asin-country-database`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching ASIN country data:', err);
      showNotification('Failed to load ASIN database', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterAndSort = () => {
    let filtered = data;

    // Filter based on search term
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      filtered = data.filter(item =>
        item.asin?.toLowerCase().includes(searchLower) ||
        item.country_of_origin?.toLowerCase().includes(searchLower) ||
        item.hts_code?.toLowerCase().includes(searchLower) ||
        item.updated_by?.toLowerCase().includes(searchLower) ||
        item.notes?.toLowerCase().includes(searchLower)
      );
    }

    // Sort data
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'asin':
          aVal = a.asin || '';
          bVal = b.asin || '';
          break;
        case 'country':
          aVal = a.country_of_origin || '';
          bVal = b.country_of_origin || '';
          break;
        case 'updated':
          aVal = new Date(a.last_updated || 0);
          bVal = new Date(b.last_updated || 0);
          break;
        case 'created':
          aVal = new Date(a.created_at || 0);
          bVal = new Date(b.created_at || 0);
          break;
        default:
          return 0;
      }

      if (sortBy === 'updated' || sortBy === 'created') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else {
        return sortOrder === 'asc' ?
          aVal.toString().localeCompare(bVal.toString()) :
          bVal.toString().localeCompare(aVal.toString());
      }
    });

    setFilteredData(filtered);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = moment(dateString);
    return {
      full: date.format('MMM D, YYYY [at] h:mm A'),
      short: date.format('MMM D, YYYY'),
      relative: date.fromNow()
    };
  };

  const parseOriginPhotos = (originPhotosJson) => {
    try {
      return JSON.parse(originPhotosJson || '[]');
    } catch {
      return [];
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenImageDialog(true);
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setSelectedImage(null);
  };

  // Get country badge styling
  const getCountryConfig = (country) => {
    const countryConfigs = {
      'CN': { variant: 'warning', label: 'China', flag: '🇨🇳' },
      'US': { variant: 'info', label: 'United States', flag: '🇺🇸' },
      'CA': { variant: 'success', label: 'Canada', flag: '🇨🇦' },
      'MX': { variant: 'error', label: 'Mexico', flag: '🇲🇽' },
      default: { variant: 'default', label: country || 'Unknown', flag: '🌍' }
    };
    return countryConfigs[country?.toUpperCase()] || countryConfigs.default;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Notification */}
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">ASIN Country Database</h1>
            <p className="text-gray-600">Comprehensive database of product origins and compliance information</p>
          </div>
          <Button
            onClick={fetchASINData}
            variant="outline"
            disabled={loading}
            className="flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Controls */}
        <Card className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4 mb-4">
            {/* Search */}
            <div className="flex-1">
              <Input
                placeholder="Search by ASIN, country, HTS code, employee, or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={Search}
                className="text-base"
              />
            </div>

            <div className="text-sm text-gray-500 whitespace-nowrap">
              {filteredData.length} of {data.length} records
            </div>
          </div>

          {/* Sort Controls */}
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="text-gray-600 font-medium">Sort by:</span>
            {[
              { key: 'updated', label: 'Last Updated', icon: Calendar },
              { key: 'created', label: 'Created', icon: Calendar },
              { key: 'asin', label: 'ASIN', icon: Hash },
              { key: 'country', label: 'Country', icon: Globe }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => handleSort(key)}
                className={`px-3 py-1.5 rounded-lg transition-colors flex items-center ${
                  sortBy === key
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <Icon className="w-3 h-3 mr-1" />
                {label}
                {sortBy === key && (
                  sortOrder === 'asc' ?
                    <ChevronUp className="w-3 h-3 ml-1" /> :
                    <ChevronDown className="w-3 h-3 ml-1" />
                )}
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" className="mb-4" />
          <div className="ml-4">
            <h3 className="text-lg font-medium text-gray-900">Loading ASIN database...</h3>
            <p className="text-gray-500">Please wait while we fetch the latest data.</p>
          </div>
        </div>
      )}

      {/* Database Table */}
      {!loading && (
        <Card className="overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Product Origin Records</h2>
              <Badge variant="default">
                {filteredData.length} records
              </Badge>
            </div>
          </div>

          {filteredData.length === 0 ? (
            <div className="text-center py-20">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Records Found</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {searchTerm
                  ? `No ASIN records match your search for "${searchTerm}". Try different keywords.`
                  : "No ASIN country data available."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ASIN
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Country of Origin
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      HTS Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Origin Photos
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.map((row, index) => {
                    const originPhotos = parseOriginPhotos(row.origin_photos);
                    const countryConfig = getCountryConfig(row.country_of_origin);
                    const updatedInfo = formatDate(row.last_updated);
                    const createdInfo = formatDate(row.created_at);

                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <Hash className="w-4 h-4 mr-2 text-gray-400" />
                            <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                              {row.asin || 'N/A'}
                            </code>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">{countryConfig.flag}</span>
                            <Badge variant={countryConfig.variant}>
                              {countryConfig.label}
                            </Badge>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          {row.hts_code ? (
                            <code className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded font-mono">
                              {row.hts_code}
                            </code>
                          ) : (
                            <span className="text-sm text-gray-500">No HTS code</span>
                          )}
                        </td>

                        <td className="px-6 py-4">
                          {originPhotos.length > 0 ? (
                            <div className="flex items-center space-x-2">
                              <div className="flex -space-x-2 overflow-hidden">
                                {originPhotos.slice(0, 3).map((photo, photoIndex) => (
                                  <button
                                    key={photoIndex}
                                    onClick={() => handleImageClick(photo.url)}
                                    className="inline-block h-8 w-8 rounded-full border-2 border-white hover:scale-110 transition-transform"
                                  >
                                    <img
                                      className="h-full w-full rounded-full object-cover"
                                      src={photo.url}
                                      alt={`Origin proof ${photoIndex + 1}`}
                                    />
                                  </button>
                                ))}
                              </div>
                              <div className="flex items-center space-x-1">
                                <Camera className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-600">
                                  {originPhotos.length}
                                </span>
                              </div>
                              {originPhotos.length > 3 && (
                                <span className="text-xs text-gray-500">
                                  +{originPhotos.length - 3} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center text-sm text-gray-500">
                              <Image className="w-4 h-4 mr-1" />
                              <span>No photos</span>
                            </div>
                          )}
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center text-sm">
                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                            <div>
                              <div className="text-sm text-gray-900">{updatedInfo.short}</div>
                              <div className="text-xs text-gray-500">{updatedInfo.relative}</div>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-2 text-gray-400" />
                            <Badge variant="default" className="text-xs">
                              {row.updated_by || 'Unknown'}
                            </Badge>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          {row.notes ? (
                            <div className="flex items-start">
                              <FileText className="w-4 h-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-gray-900 max-w-xs line-clamp-2">
                                {row.notes}
                              </p>
                            </div>
                          ) : (
                            <div className="flex items-center text-sm text-gray-500">
                              <FileText className="w-4 h-4 mr-1" />
                              <span>No notes</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Summary Stats */}
      {!loading && filteredData.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center">
              <Package className="w-8 h-8 text-blue-500 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Total ASINs</p>
                <p className="text-2xl font-bold text-gray-900">{filteredData.length.toLocaleString()}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center">
              <Globe className="w-8 h-8 text-green-500 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Countries</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(filteredData.map(item => item.country_of_origin).filter(Boolean)).size}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center">
              <Camera className="w-8 h-8 text-purple-500 mr-3" />
              <div>
                <p className="text-sm text-gray-500">With Photos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filteredData.filter(item => parseOriginPhotos(item.origin_photos).length > 0).length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center">
              <Hash className="w-8 h-8 text-orange-500 mr-3" />
              <div>
                <p className="text-sm text-gray-500">With HTS Codes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filteredData.filter(item => item.hts_code).length}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Image Preview Dialog */}
      {openImageDialog && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-4xl max-h-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Origin Proof Photo</h3>
              <Button
                variant="ghost"
                onClick={handleCloseImageDialog}
                className="p-2"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 text-center">
              <img
                src={selectedImage}
                alt="Origin proof full size"
                className="max-w-full max-h-[80vh] object-contain mx-auto"
              />
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-center">
              <Button
                variant="outline"
                onClick={() => window.open(selectedImage, '_blank')}
                className="flex items-center"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in New Tab
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default ASINCountryDatabase;